import type React from "react";
import type { Country } from "../../types";
import type { OrderFormValues } from "../../types/cmrpreview";
import { Vehicle } from "../../types/vehicles";

type CountryCode = { _id: string } | string | undefined | null;

export const CmrPreview: React.FC<{
  shipment: OrderFormValues;
  countries: Country[];
  trucks: Vehicle[];
  trailers: Vehicle[];
}> = ({ shipment, countries, trucks, trailers }) => {
  const countryOptions = (countries ?? []).flat().map((country) => ({
    value: country._id,
    label: country.name,
  }));

  const getCountryName = (codeOrName: CountryCode): string => {
    if (!codeOrName) return "";
    const countryCode =
      typeof codeOrName === "string" ? codeOrName : codeOrName._id;
    const country = countryOptions.find(
      (c) => c.value.toLowerCase() === countryCode.toLowerCase(),
    );
    return (country && country.label) || "";
  };
  return (
    <div className="space-y-1 break-words">
      {/* Header */}
      <div className="text-center mb-4 border-b pb-2">
        <h2 className="font-bold text-sm">
          International Consignment Note - CMR
        </h2>
        <p className="text-xs">
          This carriage is subject, notwithstanding any clause to the contrary,
          to the Convention on the Contract of the International Carriage of
          Goods by Road (CMR)
        </p>
      </div>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-2 gap-px items-stretch">
        {/* Left Column */}
        <div className="space-y-px">
          {/* Section 1 - Pickup */}
          <div className="border border-gray-400 p-2 min-h-[80px]">
            <div className="font-semibold text-xs mb-1">
              1. Shipper / Consignor / Sender (Name, address, country)
            </div>
            <div className="text-xs">
              <div className="font-medium">
                {shipment?.pickup?.companyName || ""}
              </div>
              <div>
                {shipment?.pickup?.address?.street?.split(",")[0] || ""}
              </div>
              <div>
                {shipment?.pickup?.address?.city || ""}{" "}
                {shipment?.pickup?.address?.postalCode || ""}
              </div>
              <div>
                {getCountryName(shipment?.pickup?.address?.country) || ""}
              </div>
            </div>
          </div>

          {/* Section 2 - Delivery */}
          <div className="border border-gray-400 p-2 min-h-[80px]">
            <div className="font-semibold text-xs mb-1">
              2. Receiver / Consignee (Name, address, country)
            </div>
            <div className="text-xs">
              <div className="font-medium">
                {shipment?.delivery?.companyName || ""}
              </div>
              <div>
                {shipment?.delivery?.address?.street?.split(",")[0] || ""}
              </div>
              <div>
                {shipment?.delivery?.address?.city || ""}{" "}
                {shipment?.delivery?.address?.postalCode || ""}
              </div>
              <div>
                {getCountryName(shipment?.delivery?.address?.country) || ""}
              </div>
            </div>
          </div>

          {/* Section 3 - Place of delivery */}
          <div className="border border-gray-400 p-2 min-h-[40px]">
            <div className="font-semibold text-xs mb-1">
              3. Place of delivery (Address, Country)
            </div>
            <div className="text-xs">
              <div className="font-medium">
                {shipment?.delivery?.companyName || ""}
              </div>
              <div>
                {shipment?.delivery?.address?.street?.split(",")[0] || ""}
              </div>
              <div>
                {shipment?.delivery?.address?.city || ""}{" "}
                {shipment?.delivery?.address?.postalCode || ""}
              </div>
              <div>
                {getCountryName(shipment?.delivery?.address?.country) || ""}
              </div>
            </div>
          </div>

          {/* Section 4 - Place of pickup */}
          <div className="border border-gray-400 p-2 min-h-[40px]">
            <div className="font-semibold text-xs mb-1">
              4. Place of pickup (Address, Country)
            </div>
            <div className="text-xs">
              <div className="font-medium">
                {shipment?.pickup?.companyName || ""}
              </div>
              <div>
                {shipment?.pickup?.address?.street?.split(",")[0] || ""}
              </div>
              <div>
                {shipment?.pickup?.address?.city || ""}{" "}
                {shipment?.pickup?.address?.postalCode || ""}
              </div>
              <div>
                {getCountryName(shipment?.pickup?.address?.country) || ""}
              </div>
            </div>
          </div>

          {/* Section 5 - Documents */}
          <div className="border border-gray-400 p-2 min-h-[30px]">
            <div className="font-semibold text-xs mb-1">
              5. Documents Attached
            </div>
            <div className="text-xs">
              {/* {shipment.attachments && shipment.attachments.length > 0 && <></>} */}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex h-full flex-col gap-px">
          {/* Section 16 - Carrier */}
          <div className="border border-gray-400 p-2 min-h-[80px]">
            <div className="font-semibold text-xs mb-1">
              16. Carrier (Name, address, country)
            </div>
            <div className="text-xs break-words">
              <div className="font-medium">
                {shipment?.carrier?.companyName || ""}
              </div>
              <div>
                {shipment?.carrier?.address?.street?.split(",")[0] || ""}
              </div>
              <div>
                {shipment?.carrier?.address?.city || ""}{" "}
                {shipment?.carrier?.address?.postalCode || ""}
              </div>
              <div>
                {getCountryName(shipment?.carrier?.address?.country) || ""}
              </div>
            </div>
          </div>

          {/* Section 17 - Successive Carrier */}
          <div className="border border-gray-400 p-2 min-h-[40px]">
            <div className="font-semibold text-xs mb-1">
              17. Successive Carrier (Name)
            </div>
            <div className="text-xs break-words">
              {shipment?.carrier?.companyName || ""}
            </div>
          </div>

          {/* Section 18 - Reservations */}
          <div className="border border-gray-400 p-2 min-h-[60px]">
            <div className="font-semibold text-xs mb-1">
              18. Carrier's reservations and observations
            </div>
            <div className="text-xs break-words"></div>
          </div>
          <div className="border border-gray-400 p-2 min-h-[99px] flex-1">
            <div className="font-semibold text-xs mb-1">ID</div>
            <div className="text-xs">{shipment.shipmentNumber || ""}</div>
          </div>
        </div>
      </div>

      {/* Full Width Sections */}
      <div className="space-y-px mt-2">
        <div>
          {/* Header row */}
          <div className="grid grid-cols-7 gap-px font-semibold text-xs">
            <div className="border border-gray-400 p-1">
              6. Marks and numbers
            </div>
            <div className="border border-gray-400 p-1">
              7. Number of packages
            </div>
            <div className="border border-gray-400 p-1">
              8. Method of packing
            </div>
            <div className="border border-gray-400 p-1">9. Nature of goods</div>
            <div className="border border-gray-400 p-1">
              10. Statistical number
            </div>
            <div className="border border-gray-400 p-1">
              11. Gross weight (kg)
            </div>
            <div className="border border-gray-400 p-1">12. Volume (m³)</div>
          </div>

          {/* Cargo items (max 5 rows) */}
          {(shipment?.cargoItems || []).slice(0, 5).map((item, index) => (
            <div key={index} className="grid grid-cols-7 gap-px text-xs">
              <div className="border border-gray-400 p-1 break-words">
                {item.marks || ""}
              </div>
              <div className="border border-gray-400 p-1">
                {item.quantity || ""}
              </div>
              <div className="border border-gray-400 p-1 break-words">
                {item.type || ""}
              </div>
              <div className="border border-gray-400 p-1 break-words">
                {item.description || ""}
              </div>
              <div className="border border-gray-400 p-1 break-words"></div>
              <div className="border border-gray-400 p-1">
                {item.weightKg || 0}
              </div>
              <div className="border border-gray-400 p-1">
                {item.volumeM3 || 0}
              </div>
            </div>
          ))}
        </div>

        {/* Instructions and Payment Row */}
        <div className="grid grid-cols-4 gap-px">
          <div className="border border-gray-400 p-2">
            <div className="font-semibold text-xs">
              13. Sender's instructions (treatment of carriage in custom.)
            </div>
            <div className="text-xs mt-1">{shipment.additionalNotes || ""}</div>
          </div>
          <div className="border border-gray-400 p-0 col-span-2">
            {/* Header row */}
            <div className="grid grid-cols-5 gap-px">
              <div className="p-2 col-span-2">
                <div className="font-semibold text-xs">19. To be paid by</div>
              </div>
              <div className="px-1 py-2">
                <div className="font-semibold text-xs">Sender</div>
              </div>
              <div className="px-1 py-2">
                <div className="font-semibold text-xs">Currency</div>
              </div>
              <div className="px-1 py-2">
                <div className="font-semibold text-xs">Consignee</div>
              </div>
            </div>

            {/* Main content area */}
            <div className="grid grid-cols-4 gap-px">
              <div className="p-2 text-xs leading-5 break-words">
                <div>Carriage charges</div>
                <div className="my-1 h-px bg-gray-400" />
                <div className="space-y-0.5">
                  <div>Deduction</div>
                  <div>Balance</div>
                  <div>Supplem charges</div>
                  <div>Other</div>
                </div>
              </div>
              <div className="p-2 col-span-3 min-h-[120px] text-xs break-words">
                {/* Intentionally left blank area for amounts/notes */}
              </div>
            </div>

            {/* Total row */}
            <div className="grid grid-cols-4 gap-px">
              <div className="p-2 text-xs font-semibold">Total</div>
              <div className="p-2 min-h-[24px]" />
              <div className="p-2 min-h-[24px]" />
              <div className="p-2 min-h-[24px]" />
            </div>
          </div>
          <div className="border border-gray-400 p-2">
            <div className="font-semibold text-xs">20. Other agreements</div>
            <div className="text-xs mt-1"></div>
          </div>
        </div>

        {/* Bottom Information Row */}
        <div className="grid grid-cols-4 gap-px">
          <div className="border border-gray-400 p-2">
            <div className="font-semibold text-xs">21. Established in</div>
            <div className="text-xs mt-1">
              {shipment?.delivery?.address?.city || ""}{" "}
              {getCountryName(shipment?.delivery?.address?.country) || ""}
            </div>
            <div className="text-xs">
              On: {shipment?.delivery?.date?.split("T")[0] || ""} /{" "}
              {shipment?.delivery?.time || ""}
            </div>
          </div>
          <div className="border border-gray-400 p-2">
            <div className="font-semibold text-xs">
              25. Truck / Trailer / Container
            </div>
            <div className="text-xs mt-1">
              <div>
                {shipment?.carrier?.truckLicensePlate
                  ? (() => {
                      const truckId =
                        typeof shipment.carrier.truckLicensePlate === "object"
                          ? shipment.carrier.truckLicensePlate._id
                          : shipment.carrier.truckLicensePlate;

                      const truck = trucks.find((v) => v._id === truckId);
                      return truck?.registrationNumber || truckId;
                    })()
                  : ""}
              </div>
              <div>
                {" "}
                {shipment?.carrier?.trailerLicensePlate
                  ? (() => {
                      const trailerId =
                        typeof shipment.carrier.trailerLicensePlate === "object"
                          ? shipment.carrier.trailerLicensePlate._id
                          : shipment.carrier.trailerLicensePlate;

                      const trailer = trailers.find((v) => v._id === trailerId);
                      return trailer?.registrationNumber || trailerId;
                    })()
                  : ""}
              </div>
              <div>{shipment?.carrier?.containerNumber || ""}</div>
            </div>
          </div>
          <div className="border border-gray-400 p-2">
            <div className="font-semibold text-xs">22. Pickup Time</div>
            <div className="text-xs mt-1">{shipment?.pickup?.time}</div>
            <div className="font-semibold text-xs mt-2">23. Leaving</div>
          </div>
          <div className="border border-gray-400 p-2">
            <div className="font-semibold text-xs">
              24. Date of goods received
            </div>
            <div className="text-xs mt-1">Arriving:</div>
            <div className="text-xs">Leaving:</div>
          </div>
        </div>

        {/* Signature Row */}
        <div className="grid grid-cols-3 gap-px">
          <div className="border border-gray-400 p-3 min-h-[60px]">
            <div className="font-semibold text-xs">
              Signature and Stamp of Sender
            </div>
          </div>
          <div className="border border-gray-400 p-3 min-h-[60px]">
            <div className="font-semibold text-xs">
              Signature, Stamp of Carrier
            </div>
          </div>
          <div className="border border-gray-400 p-3 min-h-[60px]">
            <div className="font-semibold text-xs">
              Signature and Stamp of Consignee
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
